import { Action, Dispatch } from "redux";
import { fetchResponses, fetchResponseVersions, IPublishedRequestParams } from "./Operations";
import { AuthState } from "@okta/okta-auth-js";
import { Action as AnyAction } from "@reduxjs/toolkit";

export enum ResponseActionTypes {
  FetchResponsesPending = "FETCH_RESPONSES_PENDING",
  FetchedResponses = "FETCHED_RESPONSES",
  FetchResponsesRejected = "FETCH_RESPONSES_REJECTED",
  SetSortOrder = "SET_SORT_ORDER",
  ResetSortOrder = "RESET_SORT_ORDER",
  SetFilter = "SET_FILTER",
  ClearFilter = "CLEAR_FILTER",
  ClearAllFilters = "CLEAR_ALL_FILTERS"
}

export enum ResponseVersionsActionTypes {
  FetchResponseVersionsPending = "FETCH_RESPONSE_VERSIONS_PENDING",
  FetchedResponseVersions = "FETCHED_RESPONSE_VERSIONS",
  FetchResponseVersionsRejected = "FETCH_RESPONSE_VERSIONS_REJECTED",
  ResetResponseVersions = "RESET_RESPONSE_VERSIONS"
}

export class FetchingResponsesAction implements Action {
  public readonly type = ResponseActionTypes.FetchResponsesPending;
}

export class FetchedResponsesAction implements Action {
  public readonly type = ResponseActionTypes.FetchedResponses;
  constructor(
    public payload: { responses: Api.IPagedResult<Api.IResponseListViewModel> }
  ) { }
}

export class FetchResponsesFailedAction implements Action {
  public readonly type = ResponseActionTypes.FetchResponsesRejected;
  constructor(public payload: { error: string }) { }
}

export class FetchingResponseVersionsAction implements Action {
  public readonly type = ResponseVersionsActionTypes.FetchResponseVersionsPending;
}

export class FetchedResponseVersionsAction implements Action {
  public readonly type = ResponseVersionsActionTypes.FetchedResponseVersions;
  constructor(public payload: { responses: Api.IHistoricalResponseListViewModel }) { }
}

export class FetchResponseVersionsFailedAction implements Action {
  public readonly type = ResponseVersionsActionTypes.FetchResponseVersionsRejected;
  constructor(public payload: { error: string }) { }
}

export class ResetResponseVersionsAction implements Action {
  public readonly type = ResponseVersionsActionTypes.ResetResponseVersions;
  [key: string]: any;
}

export class SetSortOrderAction implements Action {
  public readonly type = ResponseActionTypes.SetSortOrder;
  constructor(public payload: { orderBy: string }) { }
}

export class ResetSortOrderAction implements Action {
  public readonly type = ResponseActionTypes.ResetSortOrder;
}

export class SetFilterAction implements Action {
  public readonly type = ResponseActionTypes.SetFilter;
  constructor(public payload: { field: string; value: any; operator?: string }) { }
}

export class ClearFilterAction implements Action {
  public readonly type = ResponseActionTypes.ClearFilter;
  constructor(public payload: { field: string }) { }
}

export class ClearAllFiltersAction implements Action {
  public readonly type = ResponseActionTypes.ClearAllFilters;
}

export type ResponsesAction =
  | FetchingResponsesAction
  | FetchedResponsesAction
  | FetchResponsesFailedAction
  | SetSortOrderAction
  | ResetSortOrderAction
  | SetFilterAction
  | ClearFilterAction
  | ClearAllFiltersAction;

export type ResponseVersionsAction =
  | FetchingResponseVersionsAction
  | FetchedResponseVersionsAction
  | FetchResponseVersionsFailedAction
  | ResetResponseVersionsAction;

export const getResponseListThunk: (
  auth: AuthState,
  requestParams: IPublishedRequestParams
) => any = (auth, requestParams) => {
  return (dispatch: Dispatch<AnyAction>) => {
    dispatch(new FetchingResponsesAction());
    fetchResponses(auth, requestParams)
      .then((response: Api.IResponseListResponse) => {
        dispatch(new FetchedResponsesAction({ responses: response.responses }));
      })
      .catch((errorResponse: Error) => {
        // tslint:disable-next-line:no-console
        console.error(errorResponse);
        dispatch(
          new FetchResponsesFailedAction({ error: errorResponse.message })
        );
      });
  };
};

// This is a thunk action creator for setSortOrder
export const setSortOrder: (orderBy: string) => any = (orderBy) => {
  return (dispatch: Dispatch<AnyAction>) => {
    dispatch(new SetSortOrderAction({ orderBy }));
  };
};

// This is a thunk action creator for resetSortOrder
export const resetSortOrder: () => any = () => {
  return (dispatch: Dispatch<AnyAction>) => {
    dispatch(new ResetSortOrderAction());
  };
};

// Filter action creators
export const setFilter = (field: string, value: any, operator?: string) => {
  return (dispatch: Dispatch<AnyAction>) => {
    dispatch(new SetFilterAction({ field, value, operator }));
  };
};

export const clearFilter = (field: string) => {
  return (dispatch: Dispatch<AnyAction>) => {
    dispatch(new ClearFilterAction({ field }));
  };
};

export const clearAllFilters = () => {
  return (dispatch: Dispatch<AnyAction>) => {
    dispatch(new ClearAllFiltersAction());
  };
};
export const getResponseVersionsThunk: (responseId: string, auth: AuthState) => any = (responseId, auth) => {
  return (dispatch: Dispatch<Action>): Promise<any> => {
    dispatch(new FetchingResponseVersionsAction());
    return fetchResponseVersions(responseId, auth)
      .then((response: Api.IHistoricalResponseListViewModel | null) => {
        if (response?.errors && response.errors.length > 0) {
          dispatch(new FetchResponseVersionsFailedAction({ error: response.errors.map((e: any) => e.message).join(', ') }));
        } else {
          dispatch(new FetchedResponseVersionsAction({ responses: response as Api.IHistoricalResponseListViewModel }));
        }
      })
      .catch((errorResponse: Error) => {
        dispatch(new FetchResponseVersionsFailedAction({ error: errorResponse.message }));
      });
  }
}
